import { useEffect } from 'react'

import { isApiError } from '@/api/client'
import { forgetLastMatter } from '@/lastMatter'

/**
 * Stop the Case and Workbench tabs pointing at a matter the server says is not
 * there.
 *
 * `AppShell` remembers every matter path it sees before anything has checked
 * that the matter exists, so a mistyped or since-removed id would otherwise
 * become the target of both tabs for the rest of the sitting, and every click
 * on Case would end in the same "Could not load this matter" card. A 404 is the
 * server's answer that the id is wrong, so the screen that asked forgets it and
 * the next click on the tab lands on the empty state, with its way back to the
 * Queue.
 *
 * Only a 404. A timeout, a 5xx or a 403 says nothing about the matter, and
 * forgetting on those would lose a good link to a flaky network.
 *
 * `errorUpdatedAt` is a dependency so that a second 404 for the same id forgets
 * again: the shell writes the path back when a screen mounts, and the mount
 * effect of a screen runs before the shell's.
 */
export function useForgetMissingMatter(
  matterId: string | null,
  matter: { error: unknown; errorUpdatedAt: number },
): void {
  const missing = isApiError(matter.error) && matter.error.isNotFound
  const { errorUpdatedAt } = matter

  useEffect(() => {
    if (matterId && missing) forgetLastMatter(matterId)
  }, [matterId, missing, errorUpdatedAt])
}
